import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { paths } from '../app/paths';
import { ApiError } from '../api/client';
import {
  useAddSlateGame,
  useCommishOverview,
  useCreateInvite,
  useEligibleGames,
  useGenerateSlate,
  useInvites,
  useMembers,
  useOverrideGrade,
  usePublishWeek,
  useRemoveSlateGame,
  useReplaceSlateGame,
  useRerollAll,
  useRerollOne,
  useProviderQuota,
  useRefreshOdds,
  useResetMemberPassword,
  useRevokeInvite,
  useSession,
  useSetResult,
  useSettleWeek,
  useSetWeekLock,
  useSyncSchedule,
  useUnpublishWeek,
  useUpdateMember,
  useWeek,
  useWeekAudit,
  useWeekList,
} from '../api/queries';
import { asyncState, combineAsync, messageFor } from './asyncState';
import type { PendingConfirmation } from '../screens/contracts';
import type { ResetLinkDto, SettleWeekReportDto } from '@pickem/shared';
import * as screens from '../ui/screens/commish';

export function CommishHomeRoute() {
  const navigate = useNavigate();
  const overview = useCommishOverview();

  return (
    <screens.CommishHome
      {...asyncState(overview)}
      overview={overview.data ?? null}
      slateHref={paths.commishSlate}
      resultsHref={paths.commishResults}
      membersHref={paths.commishMembers}
      broadcastHref={paths.commishBroadcast}
      onOpenWeek={() => navigate(paths.commishSlate)}
    />
  );
}

/**
 * The slate generator.
 *
 * The interesting part is the confirmation flow. The server refuses a destructive edit on
 * a published slate until the caller acknowledges how many picks it would affect — and it
 * returns that count *with* the refusal. So the first attempt is allowed to fail: we catch
 * `ACKNOWLEDGEMENT_REQUIRED`, turn the server's own numbers into a specific confirmation,
 * and replay the same action with the acknowledgement set. The commissioner is never asked
 * a vague "are you sure".
 */
export function CommishSlateRoute() {
  const session = useSession();
  const seasonId = session.data?.activeSeason?.id ?? null;

  const weekList = useWeekList(seasonId);
  const overview = useCommishOverview();

  // Default to the oldest draft week — the one the commissioner most likely came to build.
  const defaultWeekId =
    overview.data?.draftWeeks[0]?.id ?? overview.data?.week?.id ?? session.data?.currentWeek?.id ?? null;
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const weekId = selectedWeekId ?? defaultWeekId;

  const week = useWeek(weekId);
  const audit = useWeekAudit(weekId);

  const generate = useGenerateSlate(weekId);
  const rerollAll = useRerollAll(weekId);
  const rerollOne = useRerollOne(weekId);
  const remove = useRemoveSlateGame(weekId);
  const replace = useReplaceSlateGame(weekId);
  const publish = usePublishWeek(weekId);
  const unpublish = useUnpublishWeek(weekId);
  const setLock = useSetWeekLock(weekId);

  // Feed controls. Deliberately two mutations rather than one: the schedule is free and
  // the spreads are metered, and a single "refresh" button would make the expensive one
  // reachable by reflex.
  const quota = useProviderQuota();
  const syncSchedule = useSyncSchedule();
  const refreshOdds = useRefreshOdds();
  const [feedError, setFeedError] = useState<string | null>(null);
  const [scheduleNote, setScheduleNote] = useState<string | null>(null);

  const seasonYear = session.data?.activeSeason?.year ?? new Date().getFullYear();

  /** Both pools, one press. The schedule is free, so there is no reason to make it a choice. */
  const pullSchedule = () => {
    setFeedError(null);
    setScheduleNote(null);
    Promise.all(
      (['nfl', 'cfb'] as const).map((level) =>
        syncSchedule.mutateAsync({ level, seasonYear }),
      ),
    ).then(
      (reports) => {
        const games = reports.reduce((sum, r) => sum + r.gamesUpserted, 0);
        const scores = reports.reduce((sum, r) => sum + r.scoresUpdated, 0);
        setScheduleNote(`${games} games and ${scores} scores refreshed. No credits spent.`);
      },
      (error: unknown) => setFeedError(messageFor(error)),
    );
  };

  const [actionError, setActionError] = useState<string | null>(null);
  const [publishWarnings, setPublishWarnings] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [lastReport, setLastReport] = useState<
    { drawn: number; buckets: { level: string; requested: number; eligible: number; drawn: number }[]; exclusionsRelaxed: boolean } | null
  >(null);

  /** Turns an ACKNOWLEDGEMENT_REQUIRED refusal into a specific confirmation prompt. */
  const handleActionError = (
    error: unknown,
    kind: PendingConfirmation['kind'],
    slateGameId: string | null,
    replay: () => void,
  ) => {
    if (error instanceof ApiError && error.code === 'ACKNOWLEDGEMENT_REQUIRED') {
      const details = error.details as { pickCount?: number } | undefined;
      const count = details?.pickCount ?? 0;
      setConfirmation({
        kind,
        slateGameId,
        pickCount: count,
        message: `${count} pick${count === 1 ? ' has' : 's have'} already been made on this game. Their picks will be kept and scored as void.`,
      });
      setPendingAction(() => replay);
      return;
    }
    if (error instanceof ApiError && error.code === 'PICKS_EXIST') {
      const details = error.details as { pickCount?: number } | undefined;
      const count = details?.pickCount ?? 0;
      setConfirmation({
        kind: 'unpublish',
        slateGameId: null,
        pickCount: count,
        message: `${count} pick${count === 1 ? '' : 's'} already exist for this week. Unpublishing keeps them, but stops anyone making new ones.`,
      });
      setPendingAction(() => replay);
      return;
    }
    setActionError(messageFor(error));
  };

  const clearConfirmation = () => {
    setConfirmation(null);
    setPendingAction(null);
  };

  return (
    <screens.CommishSlate
      {...combineAsync(week, weekList)}
      week={week.data ?? null}
      weeks={weekList.data ?? []}
      selectedWeekId={weekId}
      onSelectWeek={setSelectedWeekId}
      lastReport={lastReport}
      busy={{
        generate: generate.isPending,
        rerollAll: rerollAll.isPending,
        rerollOne: rerollOne.isPending ? (rerollOne.variables ?? null) : null,
        publish: publish.isPending,
        removing: remove.isPending ? (remove.variables?.slateGameId ?? null) : null,
      }}
      actionError={actionError}
      requiredGames={session.data?.activeSeason?.gamesPerWeek ?? 5}
      onGenerate={() => {
        setActionError(null);
        generate.mutate(undefined, {
          onSuccess: (report) => setLastReport(report),
          onError: (e) => setActionError(messageFor(e)),
        });
      }}
      onRerollAll={() => {
        setActionError(null);
        rerollAll.mutate(true, {
          onSuccess: (report) => setLastReport(report),
          onError: (e) => setActionError(messageFor(e)),
        });
      }}
      onRerollOne={(slateGameId) => {
        setActionError(null);
        rerollOne.mutate(slateGameId, { onError: (e) => setActionError(messageFor(e)) });
      }}
      onRemove={(slateGameId, reason, acknowledge) => {
        setActionError(null);
        const run = (ack: boolean) =>
          remove.mutate(
            {
              slateGameId,
              reason: reason ?? 'Removed by the commissioner',
              acknowledgePicksAffected: ack,
            },
            {
              onSuccess: clearConfirmation,
              onError: (e) =>
                handleActionError(e, 'remove', slateGameId, () => run(true)),
            },
          );
        run(acknowledge ?? false);
      }}
      onEditSpread={(slateGameId, spread, acknowledge) => {
        setActionError(null);
        const run = (ack: boolean) =>
          replace.mutate(
            { slateGameId, contestSpreadHome: spread, acknowledgePicksAffected: ack },
            {
              onSuccess: clearConfirmation,
              onError: (e) => handleActionError(e, 'spread', slateGameId, () => run(true)),
            },
          );
        run(acknowledge ?? false);
      }}
      onPublish={(allowPastKickoffs) => {
        setActionError(null);
        setPublishWarnings([]);
        publish.mutate(allowPastKickoffs ?? false, {
          onSuccess: (result) => setPublishWarnings(result.warnings),
          onError: (e) => {
            // A slate with a kickoff already in the past is publishable, but only on a
            // second, explicit press — so the warning is surfaced rather than swallowed.
            if (e instanceof ApiError && e.code === 'SLATE_INCOMPLETE') {
              const details = e.details as { issues?: string[] } | undefined;
              setPublishWarnings(details?.issues ?? []);
            }
            setActionError(messageFor(e));
          },
        });
      }}
      onUnpublish={(force) => {
        setActionError(null);
        const run = (f: boolean) =>
          unpublish.mutate(f, {
            onSuccess: clearConfirmation,
            onError: (e) => handleActionError(e, 'unpublish', null, () => run(true)),
          });
        run(force ?? false);
      }}
      onSetLock={(mode) => {
        setActionError(null);
        setLock.mutate(mode, { onError: (e) => setActionError(messageFor(e)) });
      }}
      replaceHrefFor={(slateGameId) => paths.commishReplace(slateGameId, weekId ?? undefined)}
      addGameHref={paths.commishAdd(weekId ?? undefined)}
      confirmation={confirmation}
      onConfirm={() => {
        pendingAction?.();
      }}
      onCancelConfirmation={clearConfirmation}
      feed={{
        quota: quota.data ?? null,
        isSyncingSchedule: syncSchedule.isPending,
        isPullingSpreads: refreshOdds.isPending,
        lastOdds: refreshOdds.data ?? null,
        scheduleNote,
        error: feedError,
        onSyncSchedule: pullSchedule,
        onPullSpreads: (force: boolean) => {
          setFeedError(null);
          refreshOdds.mutate(
            { seasonYear, force },
            { onError: (error: unknown) => setFeedError(messageFor(error)) },
          );
        },
      }}
      publishWarnings={publishWarnings}
      audit={audit.data ?? []}
    />
  );
}

/** Browse the eligible schedule and swap a game into a slot. */
export function CommishReplaceRoute() {
  const navigate = useNavigate();
  const params = useParams<{ slateGameId?: string }>();
  const location = useLocation();
  const session = useSession();

  const overview = useCommishOverview();
  // The week the commissioner was actually looking at, passed through the link. The
  // overview default is only a fallback for someone arriving at the URL directly.
  const weekFromQuery = new URLSearchParams(location.search).get('week');
  const weekIdFromOverview =
    weekFromQuery ??
    overview.data?.draftWeeks[0]?.id ??
    overview.data?.week?.id ??
    session.data?.currentWeek?.id ??
    null;

  const week = useWeek(weekIdFromOverview);
  const target = week.data?.games.find((g) => g.id === params.slateGameId) ?? null;

  const [filters, setFilters] = useState({
    level: '' as 'nfl' | 'cfb' | '',
    q: '',
    conference: '',
    date: '',
    page: 1,
  });

  const eligible = useEligibleGames(weekIdFromOverview, {
    level: filters.level === '' ? undefined : filters.level,
    q: filters.q || undefined,
    conference: filters.conference || undefined,
    date: filters.date || undefined,
    page: filters.page,
    pageSize: 25,
  });

  const replace = useReplaceSlateGame(weekIdFromOverview);
  const add = useAddSlateGame(weekIdFromOverview);
  const [actionError, setActionError] = useState<string | null>(null);

  // Offered as filter options, derived from what the current page actually contains.
  const conferences = useMemo(() => {
    const set = new Set<string>();
    for (const game of eligible.data?.items ?? []) {
      if (game.home.conference) set.add(game.home.conference);
      if (game.away.conference) set.add(game.away.conference);
    }
    return [...set].sort();
  }, [eligible.data]);

  const back = () => navigate(paths.commishSlate);

  return (
    <screens.CommishReplace
      {...combineAsync(week, eligible)}
      target={target}
      week={week.data ?? null}
      results={eligible.data ?? null}
      isFetching={eligible.isFetching}
      filters={filters}
      onFiltersChange={(next) =>
        // Any filter change resets to page 1; keeping the old page would show an empty
        // result set for a narrower query and read as "no games".
        setFilters((prev) => ({ ...prev, ...next, page: next.page ?? 1 }))
      }
      onChoose={(scheduleGameId) => {
        setActionError(null);
        const onError = (e: unknown) => setActionError(messageFor(e));
        if (params.slateGameId) {
          replace.mutate(
            { slateGameId: params.slateGameId, scheduleGameId, acknowledgePicksAffected: true },
            { onSuccess: back, onError },
          );
        } else {
          add.mutate({ scheduleGameId }, { onSuccess: back, onError });
        }
      }}
      onCancel={back}
      isSubmitting={replace.isPending || add.isPending}
      actionError={actionError}
      conferences={conferences}
    />
  );
}

export function CommishResultsRoute() {
  const session = useSession();
  const seasonId = session.data?.activeSeason?.id ?? null;
  const weekList = useWeekList(seasonId);

  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const weekId = selectedWeekId ?? session.data?.currentWeek?.id ?? null;

  const week = useWeek(weekId);
  const setResult = useSetResult(weekId);
  const overrideGrade = useOverrideGrade(weekId);
  const settle = useSettleWeek(weekId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [settleReport, setSettleReport] = useState<SettleWeekReportDto | null>(null);

  // Cleared whenever the week changes, so a report never describes a week other than the
  // one on screen.
  const clearFeedback = () => {
    setActionError(null);
    setSettleReport(null);
  };

  return (
    <screens.CommishResults
      {...combineAsync(week, weekList)}
      week={week.data ?? null}
      weeks={weekList.data ?? []}
      selectedWeekId={weekId}
      onSelectWeek={(id) => {
        clearFeedback();
        setSelectedWeekId(id);
      }}
      savingGameId={setResult.isPending ? (setResult.variables?.slateGameId ?? null) : null}
      actionError={actionError}
      onSaveResult={(args) => {
        clearFeedback();
        setResult.mutate(args, { onError: (e) => setActionError(messageFor(e)) });
      }}
      onOverrideGrade={(args) => {
        clearFeedback();
        overrideGrade.mutate(args, { onError: (e) => setActionError(messageFor(e)) });
      }}
      isSettling={settle.isPending}
      settleReport={settleReport}
      onSettle={() => {
        clearFeedback();
        settle.mutate(undefined, {
          onSuccess: setSettleReport,
          onError: (e) => setActionError(messageFor(e)),
        });
      }}
    />
  );
}

export function CommishMembersRoute() {
  const session = useSession();
  const leagueId = session.data?.activeLeagueId ?? null;

  const members = useMembers(leagueId);
  const invites = useInvites(leagueId);
  const updateMember = useUpdateMember(leagueId);
  const createInvite = useCreateInvite(leagueId);
  const revokeInvite = useRevokeInvite(leagueId);
  const resetPassword = useResetMemberPassword();
  const [actionError, setActionError] = useState<string | null>(null);
  // Held in component state, never in the query cache: the link is a bearer token, and
  // caching it would leave it recoverable long after the commissioner cleared the panel.
  const [resetLink, setResetLink] = useState<ResetLinkDto | null>(null);

  return (
    <screens.CommishMembers
      {...combineAsync(members, invites)}
      members={members.data ?? []}
      invites={invites.data ?? []}
      isCreatingInvite={createInvite.isPending}
      actionError={actionError}
      onChangeRole={(membershipId, role) => {
        setActionError(null);
        updateMember.mutate({ membershipId, role }, { onError: (e) => setActionError(messageFor(e)) });
      }}
      onChangeStatus={(membershipId, status) => {
        setActionError(null);
        updateMember.mutate(
          { membershipId, status },
          { onError: (e) => setActionError(messageFor(e)) },
        );
      }}
      onCreateInvite={() => {
        setActionError(null);
        createInvite.mutate({}, { onError: (e) => setActionError(messageFor(e)) });
      }}
      onRevokeInvite={(inviteId) => {
        setActionError(null);
        revokeInvite.mutate(inviteId, { onError: (e) => setActionError(messageFor(e)) });
      }}
      joinUrlFor={(code) => `${window.location.origin}${paths.join(code)}`}
      resettingMembershipId={resetPassword.isPending ? (resetPassword.variables ?? null) : null}
      resetLink={resetLink}
      onDismissResetLink={() => setResetLink(null)}
      onResetPassword={(membershipId) => {
        setActionError(null);
        setResetLink(null);
        resetPassword.mutate(membershipId, {
          onSuccess: setResetLink,
          onError: (e) => setActionError(messageFor(e)),
        });
      }}
    />
  );
}
